import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { suppliersService } from '../../services/suppliers.service';
import { pincodeService } from '../../services/pincode.service';
import { countriesService } from '../../services/countries.service';
import { statesService } from '../../services/states.service';
import { citiesService } from '../../services/cities.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';

const GRAD = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const initialSupplier = {
  supplier_code: "",
  agency_name: "",
  head_person: "",
  mobile: "",
  email: "",
  address: "",
  pincode: "",
  city: "",
  state: "",
  country: "India",
  status: "active",
  password: "",
};

const AddSupplier = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editSupplierId = location.state?.supplierId;
  const isEdit = !!editSupplierId;

  const [loading, setLoading] = useState(false);
  const [supplier, setSupplier] = useState(initialSupplier);
  // option values are names (the supplier API stores names); id is kept to bind the dependent dropdowns
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const countries = await loadCountryOptions();
      if (isEdit) loadSupplierDetail(countries);
    })();
  }, [editSupplierId]);

  const toOptions = (list) => (list || []).map(i => ({ value: i.name, label: i.name, id: i.id }));

  const loadCountryOptions = async () => {
    try {
      const res = await countriesService.list({ page: 1, status: "active" });
      const opts = toOptions(res?.resData?.data);
      setCountryOptions(opts);
      return opts;
    } catch (err) {
      setCountryOptions([]);
      return [];
    }
  }

  const loadStateOptions = async (countryId) => {
    if (!countryId) { setStateOptions([]); return []; }
    try {
      const res = await statesService.list({ page: 1, status: "active", country_id: countryId });
      const opts = toOptions(res?.resData?.data);
      setStateOptions(opts);
      return opts;
    } catch (err) {
      setStateOptions([]);
      return [];
    }
  }

  const loadCityOptions = async (stateId) => {
    if (!stateId) { setCityOptions([]); return []; }
    try {
      const res = await citiesService.list({ page: 1, status: "active", state_id: stateId });
      const opts = toOptions(res?.resData?.data);
      setCityOptions(opts);
      return opts;
    } catch (err) {
      setCityOptions([]);
      return [];
    }
  }

  // reload the dependent dropdowns for a country/state given by name
  const syncLocationOptions = async (countries, countryName, stateName) => {
    const country = countries.find(c => c.value === countryName);
    const states = await loadStateOptions(country?.id);
    const state = states.find(st => st.value === stateName);
    await loadCityOptions(state?.id);
  }

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadSupplierDetail = async (countries) => {
    try {
      setLoading(true);
      const res = await suppliersService.details(editSupplierId);
      if (res?.success) {
        const v = res.resData?.supplier ?? res.resData;
        setSupplier({
          supplier_code: v?.supplier_code || "",
          agency_name: v?.agency_name || "",
          head_person: v?.head_person || "",
          mobile: v?.mobile || "",
          email: v?.email || "",
          address: v?.address || "",
          pincode: v?.pincode || "",
          city: v?.city || "",
          state: v?.state || "",
          country: v?.country || "",
          status: v?.status || "active",
          password: "",
        });
        await syncLocationOptions(countries, v?.country, v?.state);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (e) => {
    setSupplier({ ...supplier, [e.target.name]: e.target.value });
  }

  const handleMobileChange = (e) => {
    setSupplier({ ...supplier, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) });
  }

  const handlePincodeChange = async (e) => {
    const pin = e.target.value.replace(/\D/g, "").slice(0, 6);
    setSupplier((v) => ({ ...v, pincode: pin }));
    if (pin.length !== 6) return;
    try {
      const res = await pincodeService.lookup(pin);
      if (res?.success) {
        const city = res.resData?.cities?.[0];
        const state = res.resData?.states?.[0];
        const country = res.resData?.countries?.[0];
        setSupplier((v) => ({
          ...v,
          pincode: pin,
          city: city || v.city,
          state: state || v.state,
          country: country || v.country,
        }));
        await syncLocationOptions(countryOptions, country, state);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  const handleCountryChange = (o) => {
    setSupplier({ ...supplier, country: o ? o.value : "", state: "", city: "" });
    setCityOptions([]);
    loadStateOptions(o?.id);
  }

  const handleStateChange = (o) => {
    setSupplier({ ...supplier, state: o ? o.value : "", city: "" });
    loadCityOptions(o?.id);
  }

  const validate = () => {
    if (!supplier.agency_name.trim()) return "Agency name is required";
    if (!supplier.head_person.trim()) return "Head Person name is required";
    if (!/^\d{10}$/.test(supplier.mobile)) return "Enter a valid 10 digit mobile number";
    if (!/^\S+@\S+\.\S+$/.test(supplier.email)) return "Enter a valid email address";
    if (!isEdit && supplier.password.length < 6) return "Password must be at least 6 characters";
    if (!supplier.address.trim()) return "Address is required";
    if (!/^\d{6}$/.test(supplier.pincode)) return "Enter a valid 6 digit pincode";
    if (!supplier.city.trim() || !supplier.state.trim() || !supplier.country.trim()) return "Country, state and city are required";
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update supplier?" : "Save supplier?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const { supplier_code, password, ...rest } = supplier;
      const res = isEdit
        ? await suppliersService.update(editSupplierId, rest)
        : await suppliersService.create({ ...rest, password });
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        if (isEdit) {
          navigate("/suppliers/list");
        } else {
          setSupplier(initialSupplier);
          setStateOptions([]);
          setCityOptions([]);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const labelStyle = { fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#6c757d", marginBottom: 6 };
  const inputStyle = { borderRadius: 8, border: "1px solid #dfe3ec", fontSize: 13, height: 40, background: "#fff" };
  const readOnlyStyle = { ...inputStyle, background: "#eef0f8", color: "#4a5568" };
  const selectStyles = {
    control: (base, state) => ({
      ...base, borderRadius: 8, border: state.isFocused ? "1px solid #667eea" : "1px solid #dfe3ec", minHeight: 40,
      fontSize: 13, boxShadow: state.isFocused ? "0 0 0 3px rgba(102,126,234,.15)" : "none", "&:hover": { borderColor: "#667eea" }
    }),
    option: (base, state) => ({
      ...base, fontSize: 13,
      background: state.isSelected ? "#667eea" : state.isFocused ? "#eef0ff" : "#fff",
      color: state.isSelected ? "#fff" : "#1f2a44",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  };
  const sectionCard = { background: "#fff", borderRadius: 12, border: "1px solid #e8ebf7", padding: 20, marginBottom: 16 };
  const sectionTitle = (icon, text) => (
    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ fontSize: 14, color: "#4b3f8f" }}>
      <i className={"bi " + icon} style={{ color: "#667eea" }}></i> {text}
    </h6>
  );
  const field = (label, required, node, cls = "col-12 col-md-6 col-lg-4") => (
    <div className={cls}>
      <div style={labelStyle}>{label}{required && <span className="text-danger"> *</span>}</div>
      {node}
    </div>
  );
  const title = isEdit ? "Update Supplier" : "Add Supplier";
  const pageTitle = isEdit ? "Update Supplier" : "Add New Supplier";

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "#1a202c", fontSize: 20 }}>{pageTitle}</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#8a9ab5" }}>
              <Link to="/" style={{ color: "#8a9ab5" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/suppliers/list" style={{ color: "#8a9ab5" }}>Suppliers</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>{title}</span>
            </nav>
          </div>
          <Link to="/suppliers/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Suppliers List
          </Link>
        </div>

        <form onSubmit={handleSubmit}>
            <div style={{ ...sectionCard, padding: "20px 20px 24px" }}>
              {sectionTitle("bi-building", "Basic Supplier Details")}
              <div className="row g-3">
                {field("Agency Name", true,
                  <input type="text" className="form-control" placeholder="Your agency's name" name="agency_name"
                    value={supplier.agency_name} onChange={handleChange} style={inputStyle} />
                )}
                {field("Head Person", true,
                  <input type="text" className="form-control" placeholder="Head person name" name="head_person"
                    value={supplier.head_person} onChange={handleChange} style={inputStyle} />
                )}
                {field("Email ID", true,
                  <input type="email" className="form-control" placeholder="name@company.com" name="email"
                    value={supplier.email} onChange={handleChange} style={inputStyle} />
                )}
                {field("Mobile Number", true,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="10-digit mobile number" name="mobile"
                    value={supplier.mobile} onChange={handleMobileChange} style={inputStyle} />
                )}
                {!isEdit && field("Password", true,
                  <input type="password" className="form-control" placeholder="Minimum 6 characters" name="password" autoComplete="new-password"
                    value={supplier.password} onChange={handleChange} style={inputStyle} />
                )}
                {isEdit && field("Supplier Code", false,
                  <input type="text" className="form-control" name="supplier_code"
                    value={supplier.supplier_code} readOnly
                    style={{ ...readOnlyStyle, textTransform: "uppercase" }} />
                )}
                {field("Office Address", true,
                  <input type="text" className="form-control" placeholder="Street, area, landmark" name="address"
                    value={supplier.address} onChange={handleChange} style={inputStyle} />
                )}
                {field("Country", true,
                  <Select
                    options={countryOptions}
                    value={supplier.country ? { value: supplier.country, label: supplier.country } : null}
                    onChange={handleCountryChange}
                    placeholder="Select country"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("State", true,
                  <Select
                    options={stateOptions}
                    value={supplier.state ? { value: supplier.state, label: supplier.state } : null}
                    onChange={handleStateChange}
                    placeholder="Select state"
                    isClearable
                    isDisabled={!supplier.country}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("City", true,
                  <Select
                    options={cityOptions}
                    value={supplier.city ? { value: supplier.city, label: supplier.city } : null}
                    onChange={(o) => setSupplier({ ...supplier, city: o ? o.value : "" })}
                    placeholder="Select city"
                    isClearable
                    isDisabled={!supplier.state}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Pincode", true,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="6-digit pincode" name="pincode"
                    value={supplier.pincode} onChange={handlePincodeChange} style={inputStyle} />
                )}
                {field("Status", false,
                  <Select
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find(o => o.value === supplier.status) || null}
                    onChange={(o) => setSupplier({ ...supplier, status: o ? o.value : "active" })}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <Link to="/suppliers/list" className="btn px-4 d-flex align-items-center"
                style={{ background: "#fff", border: "1px solid #dfe3ec", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                Back
              </Link>
              <button type="submit" className="btn px-4 text-white fw-semibold"
                style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
                {title}
              </button>
            </div>

        </form>
      </section>
    </>
  );
}

export default AddSupplier;
